
import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAdelantoCart } from '@/contexts/AdelantoCartContext';
import { adelantosService } from '@/lib/adelantos-service';
import { ADQUIRENTE_STYLES } from './types';
import CartOtpStep from './CartOtpStep';
import { useActivation, ACTIVATION_STEPS } from '@/contexts/ActivationContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const fmt = (n: number) => `$${Math.abs(n).toLocaleString('es-CL')}`;

type CartStep = 'summary' | 'otp' | 'confirmed';

export default function CartWidget() {
  const { cartItems, selections, totalRecibiras, totalComision, totalTye, totalCuotaCount, removeVenta, clearCart, notifySolicitudConfirmada, montoMinimo } =
    useAdelantoCart();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<CartStep>('summary');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpInitialCountdown, setOtpInitialCountdown] = useState<number | null>(null);

  const { isStepComplete } = useActivation();
  const allComplete = ACTIVATION_STEPS.filter((s) => s.required).every((s) => isStepComplete(s.key));
  const nextPendingStep = ACTIVATION_STEPS.filter((s) => s.required).find((s) => !isStepComplete(s.key));

  const totalBruto = totalRecibiras - totalComision;
  const netRecibiras = totalRecibiras + totalTye;
  const isEmpty = cartItems.length === 0;
  const belowMinimum = montoMinimo > 0 && !isEmpty && totalBruto < montoMinimo;

  const getCuotaIds = useCallback(() => {
    return Object.values(selections)
      .flat()
      .filter((id) => id !== '__parent__');
  }, [selections]);

  const handleSendOtp = async () => {
    setSendingOtp(true);
    setError(null);
    try {
      await adelantosService.enviarOtp({ cuotaIds: getCuotaIds() });
      setOtpInitialCountdown(null);
      setStep('otp');
    } catch (err: any) {
      const message: string = err?.message ?? '';
      const cooldownMatch = err?.status === 400 ? message.match(/esperar\s+(\d+)\s+segundos/i) : null;
      if (cooldownMatch) {
        setOtpInitialCountdown(Number(cooldownMatch[1]));
        setStep('otp');
        return;
      }
      setError(message || 'No se pudo enviar el código de verificación. Intenta nuevamente.');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    await adelantosService.enviarOtp({ cuotaIds: getCuotaIds() });
  };

  const handleOtpVerified = async (otpCode: string) => {
    setConfirming(true);
    setOtpError(null);
    try {
      await adelantosService.crearSolicitud({ cuotaIds: getCuotaIds(), otpCode });
      setStep('confirmed');
      clearCart();
      notifySolicitudConfirmada();
      setTimeout(() => {
        setStep('summary');
        setOpen(false);
      }, 2500);
    } catch (err: any) {
      setOtpError(err?.message || 'Código incorrecto o solicitud inválida. Intenta nuevamente.');
    } finally {
      setConfirming(false);
    }
  };

  const handleBack = () => {
    setStep('summary');
    setOtpError(null);
  };

  const handleClose = () => {
    setOpen(false);
    if (step === 'otp') {
      setStep('summary');
      setOtpError(null);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-4 bg-slate-50 dark:bg-slate-800 p-2 pl-4 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm hover:bg-slate-100
dark:hover:bg-slate-700 transition-colors"
      >
        <div className="flex flex-col items-end mr-2">
          <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
            Total a recibir
          </span>
          <span className="text-sm font-bold text-slate-900 dark:text-white">
            {isEmpty ? '$0' : fmt(netRecibiras)}
          </span>
        </div>
        <div className="bg-primary hover:bg-blue-700 text-white w-10 h-10 rounded-lg flex items-center justify-center transition-colors shadow-sm relative">
          <span className="material-symbols-outlined text-sm">shopping_bag</span>
          {totalCuotaCount > 0 && (
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center border-2 border-white
dark:border-slate-900">
              {totalCuotaCount}
            </div>
          )}
        </div>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={handleClose} />
          <div className="absolute right-0 top-full mt-4 w-[420px] bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden
z-50">
            {step === 'confirmed' ? (
              <div className="flex flex-col items-center py-12 px-6 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                  <span className="material-symbols-outlined text-3xl text-emerald-600 dark:text-emerald-400">
                    check_circle
                  </span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
                  Solicitud enviada
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Tu solicitud fue recibida y está en revisión.
                </p>
              </div>
            ) : step === 'otp' ? (
              <CartOtpStep
                onVerified={handleOtpVerified}
                onBack={handleBack}
                onResend={handleResendOtp}
                submitting={confirming}
                externalError={otpError}
                initialCountdown={otpInitialCountdown}
              />
            ) : (
              <div className="flex flex-col">
                <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/20">
                  <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">list_alt</span>
                    Resumen de Selección
                  </h3>
                  {totalCuotaCount > 0 && (
                    <span className="bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full border border-primary/20">
                      {totalCuotaCount} {totalCuotaCount === 1 ? 'item' : 'items'}
                    </span>
                  )}
                </div>

                <div className="max-h-[240px] overflow-y-auto p-5 space-y-4">
                  {isEmpty ? (
                    <div className="flex flex-col items-center py-8 text-center">
                      <span className="material-symbols-outlined text-3xl text-slate-300 dark:text-slate-600 mb-2">
                        shopping_bag
                      </span>
                      <p className="text-sm text-slate-400 dark:text-slate-500">
                        No hay cuotas seleccionadas
                      </p>
                    </div>
                  ) : (
                    cartItems.map((item) => {
                      const style = ADQUIRENTE_STYLES[item.adquirente] ?? {
                        code: item.adquirente.slice(0, 2).toUpperCase(),
                        bgColor: 'bg-slate-100',
                        textColor: 'text-slate-600',
                      };
                      return (
                        <div key={item.ventaId} className="flex justify-between items-start group/item">
                          <div className="flex gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg ${style.bgColor} flex items-center justify-center ${style.textColor} font-bold text-xs shrink-0`}
                            >
                              {style.code}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                Venta {item.fechaVenta}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {item.selectedCuotaCount}{' '}
                                {item.selectedCuotaCount === 1 ? 'cuota seleccionada' : 'cuotas seleccionadas'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-slate-900 dark:text-white">
                              {item.totalBruto > 0 ? fmt(item.totalBruto) : '---'}
                            </p>
                            <button
                              onClick={() => removeVenta(item.ventaId)}
                              className="text-[10px] text-red-500 hover:text-red-700 opacity-0 group-hover/item:opacity-100 transition-opacity font-medium mt-1"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {!isEmpty && (
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-5 border-t border-slate-100 dark:border-slate-800">
                    <div className="space-y-2 mb-4">
                      <TooltipProvider delayDuration={150}>
                        <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400">
                          <span className="flex items-center gap-1">
                            Subtotal
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  aria-label="Más información sobre Subtotal"
                                  className="inline-flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                >
                                  <span className="material-symbols-outlined leading-none" style={{ fontSize: '17px' }}>info</span>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[260px] text-xs">
                                Suma de las cuotas seleccionadas, antes de descontar comisión y TyE.
                              </TooltipContent>
                            </Tooltip>
                          </span>
                          <span className="font-medium">{fmt(totalBruto)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400">
                          <span className="flex items-center gap-1">
                            Comisión Total
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  aria-label="Más información sobre Comisión Total"
                                  className="inline-flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                >
                                  <span className="material-symbols-outlined leading-none" style={{ fontSize: '17px' }}>info</span>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[260px] text-xs">
                                Comisión que cobra Junngla por adelantar las cuotas seleccionadas. Se descuenta del total a recibir e incluye el IVA aplicable.
                              </TooltipContent>
                            </Tooltip>
                          </span>
                          <span className="font-medium text-red-500">-{fmt(Math.abs(totalComision))}</span>
                        </div>
                        <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400">
                          <span className="flex items-center gap-1">
                            Timbre y Estampillas
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  aria-label="Más información sobre Timbre y Estampillas"
                                  className="inline-flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                >
                                  <span className="material-symbols-outlined leading-none" style={{ fontSize: '17px' }}>info</span>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[260px] text-xs">
                                Impuesto de Timbres y Estampillas (ITE) que grava las operaciones de crédito de dinero según la ley chilena. Se calcula sobre el monto del adelanto y se descuenta del total a recibir.
                              </TooltipContent>
                            </Tooltip>
                          </span>
                          <span className="font-medium text-red-500">-{fmt(Math.abs(totalTye))}</span>
                        </div>
                      </TooltipProvider>
                      <div className="my-2 border-t border-slate-200 dark:border-slate-700 border-dashed" />
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-slate-900 dark:text-white">
                          Total a Recibir
                        </span>
                        <span className="text-lg font-bold text-primary">{fmt(netRecibiras)}</span>
                      </div>
                    </div>
                    {error && (
                      <p className="text-xs text-red-500 mb-3 text-center">{error}</p>
                    )}
                    {!allComplete ? (
                      <div className="space-y-3">
                        <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200
dark:border-amber-800 rounded-lg px-3 py-2.5">
                          <span className="material-symbols-outlined text-base flex-shrink-0">warning</span>
                          <span>Para crear una solicitud de adelanto, completa la configuración de tu cuenta.</span>
                        </div>
                        <Link
                          to={nextPendingStep?.href ?? '/configuraciones/resumen'}
                          className="w-full bg-slate-700 hover:bg-slate-800 text-white py-3 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2"
                        >
                          Completar Configuración
                          <span className="material-symbols-outlined text-sm">settings</span>
                        </Link>
                      </div>
                    ) : (
                      <>
                        {belowMinimum && (
                          <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2.5 mb-3">
                            <span className="material-symbols-outlined text-base flex-shrink-0">warning</span>
                            <span>El monto mínimo para solicitar un adelanto es de ${montoMinimo.toLocaleString('es-CL')}.</span>
                          </div>
                        )}
                        <button
                          onClick={handleSendOtp}
                          disabled={sendingOtp || belowMinimum}
                          className="w-full bg-primary hover:bg-blue-700 text-white py-3 rounded-lg text-sm font-bold shadow-lg shadow-primary/20 transition-all active:scale-[0.98]
flex items-center justify-center gap-2 disabled:opacity-70"
                        >
                          {sendingOtp ? (
                            <>
                              <span className="material-symbols-outlined text-sm animate-spin">
                                progress_activity
                              </span>
                              Enviando código...
                            </>
                          ) : (
                            <>
                              Confirmar y Solicitar
                              <span className="material-symbols-outlined text-sm">check_circle</span>
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
